import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Overline, Reveal } from "@/components/Reveal";
import { NEIGHBOURHOODS } from "@/data/campaign";

const Ward11 = () => {
  const [active, setActive] = useState(0);
  const n = NEIGHBOURHOODS[active];

  return (
    <main data-testid="ward11-page" className="pt-28 lg:pt-32">
      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Overline>Our Community</Overline>
          <h1 className="mt-4 max-w-3xl font-serif text-4xl font-medium leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
            Distinct communities. <span className="italic text-terra-500">One shared voice.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink/75 sm:text-lg">
            Ward 11 is home to distinct and proud communities. Each has its own character, history, and challenges.
            What they share is the right to a councillor who listens before decisions are made — not after.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-10 lg:grid-cols-12" data-testid="neighbourhood-explorer">
          <Reveal className="lg:col-span-4">
            <div className="flex flex-col border-t border-pine-700/15">
              {NEIGHBOURHOODS.map((hood, i) => (
                <button
                  key={hood.name}
                  data-testid={`neighbourhood-tab-${hood.name.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setActive(i)}
                  className={`border-b border-pine-700/15 px-4 py-6 text-left transition-colors ${
                    active === i ? "bg-pine-900 text-paper" : "hover:bg-parch"
                  }`}
                >
                  <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${active === i ? "text-terra-400" : "text-terra-500"}`}>
                    {hood.tag}
                  </span>
                  <span className="mt-1 block font-serif text-2xl font-medium tracking-tight">{hood.name}</span>
                </button>
              ))}
            </div>
          </Reveal>

          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={n.name}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                data-testid="neighbourhood-panel"
              >
                <div className="img-frame overflow-hidden">
                  <img src={n.img} alt={n.name} className="h-80 w-full object-cover sm:h-[420px]" />
                </div>
                <div className="mt-8 grid gap-6 sm:grid-cols-[1fr_2fr]">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-terra-500">{n.tag}</p>
                    <h2 className="mt-2 font-serif text-3xl font-medium tracking-tight">{n.name}</h2>
                  </div>
                  <p className="text-base leading-relaxed text-ink/75">{n.desc}</p>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        <Reveal className="my-24 grid gap-10 border-y border-pine-700/15 py-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Overline>What They Share</Overline>
            <h2 className="mt-4 font-serif text-3xl font-medium leading-tight tracking-tight sm:text-4xl">
              Every resident deserves to be heard <span className="italic text-terra-500">and respected.</span>
            </h2>
          </div>
          <div className="lg:col-span-7">
            <p className="text-base leading-relaxed text-ink/75 sm:text-lg">
              Whether the issue is development pressure, traffic safety, rising costs, protecting heritage, or keeping
              local services strong — your street, your neighbours, and your experience come first.
            </p>
            <Link
              to="/get-involved?tab=priority"
              data-testid="ward11-share-issue-cta"
              className="group mt-8 inline-flex items-center gap-2 bg-pine-700 px-7 py-4 text-sm font-semibold text-paper transition-colors hover:bg-terra-500"
            >
              Share an issue on your street
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default Ward11;
